
import React, {useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import {theme } from './styles/home.styles';
import { css } from 'styled-components';

interface ListingMedia {
  url: string;
  description: string;
  price?: number;
  type: 'image' | 'video';
}

interface MediaCarouselProps {
  mediaDetails: ListingMedia[];
  title: string;
  handleImageClick: (imageUrl: string) => void;
  isPremium?: boolean;
}

export const MediaContainer = styled.div<{isPremium?: boolean }>`
  position: relative;
  width: 100%;
  aspect-ratio: 4/3; 
  overflow: hidden;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%); 
`;

export const PremiumMediaContainer = styled(MediaContainer)`
  aspect-ratio: 16/9; 
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%); 
`;

const MediaItem = styled.img<{isPremium?: boolean }>`
  width: 100%;
  height: 100%;
  object-fit: cover;
  cursor: pointer;
  flex-grow: 1;
  transition: opacity 0.3s ease-in-out;
  display: block; /* Ensure it's treated as a block element */
  
  /* Force full coverage */
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  
  &.loading {
    opacity: 0.5;
  }
`;

const VideoItem = styled.video<{isPremium?: boolean }>`
  width: 100%;
  height: 100%;
  object-fit: cover;
  flex-grow: 1;
  cursor: pointer;
  display: block; /* Ensure it's treated as a block element */
  
  /* Force full coverage - same as image */
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  
  /* Ensure video controls don't break the layout */
  &::-webkit-media-controls-panel {
    background: linear-gradient(transparent, rgba(0,0,0,0.3));
  }
`;

const NoMediaContainer = styled.div`
  height: 200px;
  background-color: #f0f0f0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const NavigationButton = styled.button<{direction: 'left' | 'right' }>`
  position: absolute;
  top: 50%;
  ${({direction }) => (direction === 'left' ? 'left: 10px' : 'right: 10px')};
  transform: translateY(-50%);
  background: rgba(0, 0, 0, 0.5);
  color: white;
  border: none;
  border-radius: 50%;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
  opacity: 0;
  transition: opacity 0.3s ease;

  ${MediaContainer}:hover & {
    opacity: 1;
  }
`;

const DotsContainer = styled.div`
  position: absolute;
  bottom: 10px;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  gap: 5px;
  z-index: 10;
`;

const Dot = styled.div<{active: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${({active }) => (active ? theme.colors.primary : 'rgba(25, 11, 148, 0.5)')};
  cursor: pointer;
  transition: background-color 0.3s ease;
`;

export const MediaCarouselWrapper = styled.div`
  .media-item img,
  .media-item video {
    object-fit: cover !important; 
    width: 100% !important;
    height: 100% !important;
  }
`;

export const MediaCarousel: React.FC<MediaCarouselProps> = ({
  mediaDetails,
  title,
  handleImageClick,
  isPremium = false,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [autoPlay, setAutoPlay] = useState(true);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const [imageLoaded, setImageLoaded] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (!autoPlay || mediaDetails.length <= 1) return;

    const currentMedia = mediaDetails[currentIndex];
    if (currentMedia?.type !== 'image') return;

    const interval = setInterval(() => {
      autoGoToNext();
    }, 5000);

    return () => clearInterval(interval);
  }, [currentIndex, autoPlay, mediaDetails]);

  // Ensure video plays when it becomes the current media
  useEffect(() => {
    const currentMedia = mediaDetails[currentIndex];
    if (currentMedia?.type === 'video' && videoRef.current) {
      // Reset and play video
      videoRef.current.currentTime = 0;
      videoRef.current.play().catch(error => {
        console.log('Video autoplay failed:', error);
      });
    }
    if (currentMedia?.type === 'image') {
      setImageLoaded(false);
    }
  }, [currentIndex, mediaDetails]);

  if (!mediaDetails || mediaDetails.length === 0) {
    return (
      <MediaContainer isPremium={isPremium}>
        <NoMediaContainer>No media available</NoMediaContainer>
      </MediaContainer>
    );
  }

  const autoGoToPrevious = () => {
    setCurrentIndex(prevIndex =>
      prevIndex === 0 ? mediaDetails.length - 1 : prevIndex - 1
    );
  };

  const autoGoToNext = () => {
    setCurrentIndex(prevIndex =>
      prevIndex === mediaDetails.length - 1 ? 0 : prevIndex + 1
    );
  };

  const manualGoToPrevious = (e: React.MouseEvent) => {
    e.stopPropagation();
    setAutoPlay(false);
    autoGoToPrevious();
  };

  const manualGoToNext = (e: React.MouseEvent) => {
    e.stopPropagation();
    setAutoPlay(false);
    autoGoToNext();
  };

  const manualGoToIndex = (e: React.MouseEvent, index: number) => {
    e.stopPropagation();
    setAutoPlay(false);
    setCurrentIndex(index);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (touchStart - touchEnd > 100) {
      // Swipe left, go to next
      setAutoPlay(false);
      autoGoToNext();
    }

    if (touchStart - touchEnd < -100) {
      // Swipe right, go to previous
      setAutoPlay(false);
      autoGoToPrevious();
    }
  };

  const currentMedia = mediaDetails[currentIndex];

  return (
    <MediaContainer
      isPremium={isPremium}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {currentMedia.type === 'image' ? (
        <MediaItem
          src={currentMedia.url}
          alt={`${title} - ${currentMedia.description}`}
          onClick={() => handleImageClick(currentMedia.url)}
          isPremium={isPremium}
          loading="lazy"
          className={imageLoaded ? '' : 'loading'}
          onLoad={() => setImageLoaded(true)}
          onError={() => setImageLoaded(true)} // Fallback if image fails to load
        />
      ) : (
        <VideoItem
          ref={videoRef}
          src={currentMedia.url}
          onClick={(e) => {
            e.stopPropagation();
            if (videoRef.current) {
              if (videoRef.current.paused) {
                videoRef.current.play();
              } else {
                videoRef.current.pause();
              }
            }
          }}
          isPremium={isPremium}
          controls
          autoPlay
          muted
          loop={mediaDetails.length === 1}
          playsInline
          onEnded={() => { if (autoPlay && mediaDetails.length > 1) autoGoToNext(); }}
          onLoadedData={() => {
            // Ensure video is properly loaded and fills container
            if (videoRef.current) {
              videoRef.current.style.objectFit = 'cover';
            }
          }}
        />
      )}

      {mediaDetails.length > 1 && (
        <>
          <NavigationButton direction="left" onClick={manualGoToPrevious}>
            &lt;
          </NavigationButton>
          <NavigationButton direction="right" onClick={manualGoToNext}>
            &gt;
          </NavigationButton>
          <DotsContainer>
            {mediaDetails.map((_, index) => (
              <Dot
                key={index}
                active={index === currentIndex}
                onClick={(e) => manualGoToIndex(e, index)}
              />
            ))}
          </DotsContainer>
        </>
      )}
    </MediaContainer>
  );
};